import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Switch,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Alert,
    Autocomplete,
    TextField,
    Chip,
} from "@mui/material";
import { X, Mail, BellRing, Send } from "lucide-react";
import axiosInstance from "../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../services/apiEndPoints";

// Must match MAX_LEAD_NOTIFICATION_RECIPIENTS in the API (user-controller)
const MAX_RECIPIENTS = 20;
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || "").trim());

// Lead pipeline → Tools → Email Notifications: who is emailed when a lead is created
const LeadNotificationSettings = ({ open, onClose, userId }) => {
    const [fetching, setFetching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [teamEmails, setTeamEmails] = useState([]);
    const [message, setMessage] = useState(null); // { type, text }

    const configUrl = `${apiEndPointsConfig.autoAssignConfig}/${userId}/lead-notification-config`;

    useEffect(() => {
        if (!open || !userId) return;
        setMessage(null);
        setInputValue("");
        fetchConfig();
        fetchTeamEmails();
    }, [open, userId]);

    const fetchConfig = async () => {
        try {
            setFetching(true);
            const res = await axiosInstance.get(configUrl);
            if (res.data.success) {
                setEnabled(res.data.data.enabled);
                setRecipients(res.data.data.recipients || []);
            }
        } catch (error) {
            console.error("Error fetching notification settings:", error);
            setMessage({ type: "error", text: "Couldn't load your notification settings." });
        } finally {
            setFetching(false);
        }
    };

    // Team members' emails are offered as suggestions; any address can be typed
    const fetchTeamEmails = async () => {
        try {
            const res = await axiosInstance.get(`${apiEndPointsConfig.getTeamMembersByClientId}/${userId}`);
            if (res.data.success) {
                setTeamEmails(
                    res.data.data
                        .filter(m => m.email && m.isActive !== false)
                        .map(m => ({ email: m.email.toLowerCase(), name: m.name, role: m.role }))
                );
            }
        } catch (error) {
            console.error("Error fetching team members:", error);
        }
    };

    // Adds typed/pasted text (comma, space or semicolon separated)
    const addEmails = (raw) => {
        const parts = String(raw).split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean);
        if (!parts.length) return;
        const invalid = parts.filter(e => !isValidEmail(e));
        const valid = parts.filter(isValidEmail);
        setRecipients(prev => {
            const next = [...new Set([...prev, ...valid])];
            if (next.length > MAX_RECIPIENTS) {
                setMessage({ type: "error", text: `You can add up to ${MAX_RECIPIENTS} email addresses.` });
                return next.slice(0, MAX_RECIPIENTS);
            }
            return next;
        });
        if (invalid.length) {
            setMessage({ type: "error", text: `Not a valid email: ${invalid.join(", ")}` });
            setInputValue(invalid.join(", "));
        } else {
            setInputValue("");
        }
    };

    const handleSave = async () => {
        if (inputValue.trim()) {
            setMessage({ type: "error", text: "Press Enter to add the email you typed, or clear it, before saving." });
            return;
        }
        if (enabled && !recipients.length) {
            setMessage({ type: "error", text: "Add at least one email address to turn notifications on." });
            return;
        }
        try {
            setSaving(true);
            const res = await axiosInstance.put(configUrl, { enabled, recipients });
            if (res.data.success) onClose();
        } catch (error) {
            setMessage({ type: "error", text: error.response?.data?.message || "Couldn't save the settings." });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!recipients.length) {
            setMessage({ type: "error", text: "Add at least one email address first." });
            return;
        }
        try {
            setTesting(true);
            setMessage(null);
            const res = await axiosInstance.post(`${configUrl}/test`, { recipients });
            setMessage({ type: "success", text: res.data.message || "Test email sent." });
        } catch (error) {
            setMessage({ type: "error", text: error.response?.data?.message || "Couldn't send the test email." });
        } finally {
            setTesting(false);
        }
    };

    const suggestions = teamEmails.filter(t => !recipients.includes(t.email));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" } }}
        >
            {/* Header */}
            <Box sx={{ p: 3, background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Mail size={22} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Email Notifications</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>Get an email whenever a new lead is created</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
                    <X size={20} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 3 }}>
                {fetching ? (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress size={32} sx={{ color: "#6366F1" }} />
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2.5} pt={0.5}>
                        {/* On/off */}
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: enabled ? "#C7D2FE" : "#E5E7EB", bgcolor: enabled ? "#F5F7FF" : "#F9FAFB", display: "flex", alignItems: "center", gap: 2 }}>
                            <BellRing size={20} color={enabled ? "#4F46E5" : "#9CA3AF"} />
                            <Box flex={1}>
                                <Typography fontWeight={600} fontSize={14}>New lead emails</Typography>
                                <Typography fontSize={12.5} color="text.secondary">
                                    {enabled
                                        ? "On — the people below are emailed for every new lead."
                                        : "Off — no emails are sent."}
                                </Typography>
                            </Box>
                            <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                        </Paper>

                        {/* Recipients */}
                        <Box>
                            <Typography fontWeight={600} fontSize={14} mb={0.5}>Send to</Typography>
                            <Typography fontSize={12.5} color="text.secondary" mb={1.25}>
                                Type an email and press Enter, or pick a team member. You can paste several at once.
                            </Typography>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={suggestions}
                                getOptionLabel={(o) => (typeof o === "string" ? o : o.email)}
                                filterSelectedOptions
                                value={recipients}
                                inputValue={inputValue}
                                onInputChange={(_, value, reason) => {
                                    if (reason === "reset") return;
                                    // Separator typed/pasted: add what's before it
                                    if (/[,;\s]/.test(value) && value.trim()) addEmails(value);
                                    else setInputValue(value);
                                }}
                                onChange={(_, values, reason) => {
                                    if (reason === "removeOption" || reason === "clear") {
                                        setRecipients(values.map(v => (typeof v === "string" ? v : v.email)));
                                        return;
                                    }
                                    const last = values[values.length - 1];
                                    addEmails(typeof last === "string" ? last : last.email);
                                }}
                                onBlur={() => inputValue.trim() && addEmails(inputValue)}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.email}>
                                        <Box>
                                            <Typography fontSize={13.5} fontWeight={600}>{option.name || option.email}</Typography>
                                            <Typography fontSize={12} color="text.secondary">
                                                {option.email}{option.role ? ` · ${option.role}` : ""}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((email, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={email}
                                            size="small"
                                            label={email}
                                            sx={{ bgcolor: "#EEF2FF", color: "#3730A3", fontWeight: 600, borderRadius: "7px" }}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={recipients.length ? "Add another email…" : "name@company.com"}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                                    />
                                )}
                            />
                            <Typography fontSize={12} color="text.secondary" mt={0.75}>
                                {recipients.length}/{MAX_RECIPIENTS} addresses
                            </Typography>
                        </Box>

                        <Alert severity="info" icon={false} sx={{ borderRadius: 2.5, fontSize: 12.5, bgcolor: "#F8FAFC", color: "#475569", border: "1px solid #E2E8F0" }}>
                            Covers every new lead: added by hand, website form, Meta, Google Sheets and bulk import.
                            Leads that arrive together (like an import) come as one summary email.
                        </Alert>

                        {message && (
                            <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ borderRadius: 2.5 }}>
                                {message.text}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2.5, px: 3, bgcolor: "#f9fafb", borderTop: "1px solid #E5E7EB", justifyContent: "space-between" }}>
                <Button
                    onClick={handleTest}
                    disabled={testing || fetching || !recipients.length}
                    startIcon={testing ? <CircularProgress size={14} /> : <Send size={15} />}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#4F46E5" }}
                >
                    Send test email
                </Button>
                <Box display="flex" gap={1}>
                    <Button onClick={onClose} sx={{ color: "#6B7280", textTransform: "none", fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || fetching}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2.5, px: 3, bgcolor: "#4F46E5", boxShadow: "none", "&:hover": { bgcolor: "#4338CA" } }}
                    >
                        {saving ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Save"}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default LeadNotificationSettings;
